import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import adminBg from '../Assets/bg/adminbg.jpg';
import Loader from '../Components/Loader';
import useRegister2 from '../Hooks/RegisterHooks/useRegister2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import useGetAllUser from '../Hooks/Admin/useGetAllUser';
import { axiosApi } from '../api/setupApi';

const AddAccount = ({ darkMode, onClose, onRefresh, mode = 'create', editUser = null }) => {
 const { users, refetch } = useGetAllUser(); // ✅ include refetch
 const {
    code, setCode, password, setPassword, confirmPassword, setConfirmPassword,
    name, setName, role, setRole, error, success, setError, setSuccess, handleSubmit
  } = useRegister2();
 const [isSubmitting, setIsSubmitting] = useState(false);
 const [showPassword, setShowPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const isEditMode = mode === 'edit' && !!editUser?.id;


  // ✅ Refetch and reset code every time AddAccount is opened
  useEffect(() => {
    if (isEditMode) return;

    if (refetch) {
      refetch().then(() => {
        generateCode(); // regenerate only after latest users are fetched
      });
    }
  }, [isEditMode]); // run only on mount (when AddAccount opens)

  useEffect(() => {
  if (isEditMode) return;

  if (!code) {           
       generateCode();
  }
}, [users, code, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;

    setCode(editUser?.code ? String(editUser.code) : '');
    setName(editUser?.name || '');
    setRole(editUser?.role || '');
    setPassword('');
    setConfirmPassword('');
  }, [isEditMode, editUser, setCode, setName, setRole, setPassword, setConfirmPassword]);

  // ✅ After successful creation, refresh parent and prepare for next add
  useEffect(() => {
  if (isEditMode) return;

  if (success) {
    toast.success(success, { autoClose: 3000 });

    if (refetch) refetch();     // refresh user list locally
    if (onRefresh) onRefresh(); // also refresh ManagementAdmin
    generateCode();             // regenerate unique code for next add

    setSuccess(null);
    setIsSubmitting(false);
    handleClose();
  }

  if (error) {
    toast.error(error, { autoClose: 3000 });
    setError(null);
    setIsSubmitting(false);
  }
}, [success, error, isEditMode]);


  const formSections = [
    {
      title: 'User Information',
      fields: ['code', 'name'],
    },
    {
      title: 'Credentials',
      fields: ['password', 'confirmPassword', 'role'],
    },
  ];

  const fieldLabelMap = {
    code: 'Code',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    name: 'Name',
    role: 'Role',
  };

  const handleClose = () => {
  setPassword('');
  setConfirmPassword('');
  setName('');
  setRole('');
  onClose();
};


  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (isEditMode) {
      if (password && password !== confirmPassword) {
        toast.error('Passwords do not match');
        setIsSubmitting(false);
        return;
      }

      const parsedCode = Number(code);
      if (!Number.isInteger(parsedCode) || parsedCode <= 0) {
        toast.error('Code must be a valid number');
        setIsSubmitting(false);
        return;
      }

      if (!role) {
        toast.error('Role is required');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        code: parsedCode,
        name,
        role,
      };

      if (password) {
        payload.password = password;
      }

      try {
        const { data } = await axiosApi.put(`/auth/update_user/${editUser.id}`, payload);
        toast.success(data?.message || 'User updated successfully', { autoClose: 3000 });
        if (refetch) await refetch();
        if (onRefresh) onRefresh();
        setIsSubmitting(false);
        handleClose();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to update user', { autoClose: 3000 });
        setIsSubmitting(false);
      }
      return;
    }

    await handleSubmit(e);
  };
            


const generateCode = () => {
  let newCode;
  do {
    newCode = Math.floor(100000 + Math.random() * 900000).toString(); // 6-digit integer
  } while (users.some(user => user.code === newCode));

  setCode(newCode);
};


  return (
    <>
      <ToastContainer />
      <Container darkMode={darkMode} isExpanded={true}>
        <FormContainer darkMode={darkMode} isExpanded={true}>
          <Title darkMode={darkMode}>
            <span>{isEditMode ? 'Edit Account' : 'Add Account'}</span>
            <CloseButton onClick={handleClose} darkMode={darkMode}>
              X
            </CloseButton>
          </Title>
          <form onSubmit={handleFormSubmit}>
            {formSections.map((section, index) => (
              <CardSection darkMode={darkMode} key={index}>
                {section.title && (
                  <SectionHeader darkMode={darkMode}>{section.title}</SectionHeader>
                )}
               {section.fields.map((key) => (
   <FieldGroup key={key} darkMode={darkMode}>
    <Label htmlFor={key}>
      {fieldLabelMap[key]}
      <RequiredAsterisk>*</RequiredAsterisk>
    </Label>

    {key === 'code' ? (
      <Input
        type="number"
        id="code"
        name="code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        readOnly={!isEditMode}
        style={{
          backgroundColor: isEditMode ? '#ffffff' : '#f5f5f5',
          color: isEditMode ? '#333' : '#666',
          fontWeight: 'bold'
        }}
        aria-label="Code"
        required
      />
    ) : key === 'role' ? (
      <Select
        id={key}
        name={key}
        value={role}
        onChange={(e) => setRole(e.target.value)}
        aria-label={fieldLabelMap[key]}
        required
      >
        <option value="" disabled>Select Role</option>
        <option value="admin">Admin</option>
        <option value="noso">Noso</option>
        <option value="bohol">Bohol</option>
        <option value="siquijor">Siquijor</option>
        <option value="cebu">Cebu</option>
        <option value="nis">NIS</option>
      </Select>
    ) : key === 'password' ? (
      <PasswordInputWrapper>
        <Input
          type={showPassword ? 'text' : 'password'}
          id={key}
          name={key}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={isEditMode ? 'Enter new password (optional)' : `Enter ${fieldLabelMap[key]}`}
          aria-label={fieldLabelMap[key]}
          required={!isEditMode}
        />
      </PasswordInputWrapper>
    ) : key === 'confirmPassword' ? (
      <PasswordInputWrapper>
        <Input
          type={showConfirmPassword ? 'text' : 'password'}
          id={key}
          name={key}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={isEditMode ? 'Confirm new password (optional)' : `Enter ${fieldLabelMap[key]}`}
          aria-label={fieldLabelMap[key]}
          required={!isEditMode || Boolean(password)}
        />
      </PasswordInputWrapper>
    ) : (
      <Input
        type="text"
        id={key}
        name={key}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={`Enter ${fieldLabelMap[key]}`}
        aria-label={fieldLabelMap[key]}
        maxLength={100}
        required
      />
    )}
  </FieldGroup>
))}


              </CardSection>
            ))}
            <FormButtonContainer>
              <SubmitButton darkMode={darkMode} type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
             <LoaderOverlay>
                   <Loader />
              </LoaderOverlay>
             ) : (
               isEditMode ? 'Update Account' : 'Create Account'
             )}
           </SubmitButton>
            </FormButtonContainer>
          </form>
        </FormContainer>
      </Container>
    </>
  );
};

export default AddAccount;

// Styled Components
const Container = styled.div`
  padding: 20px;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  position: relative;
  transition: transform 0.3s ease;
  transform: ${({ isExpanded }) => (isExpanded ? 'translateX(0%)' : 'translateX(80%)')};
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5px;
  flex-grow: 1;
  overflow-y: auto;
  transition: width 0.3s ease, transform 0.3s ease;
  width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 200px)')};

  @media (max-width: 1024px) {
    width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 150px)')};
  }

  @media (max-width: 768px) {
    width: 100%;
    transform: translateX(0);
  }
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 5px;
  margin: 0 0 10px 0;
  font-size: 22px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    flex: 1;
    text-align: center;
  }
`;

const CardSection = styled.div`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: ${({ darkMode }) => (darkMode ? '0 3px 8px rgba(255, 255, 255, 0.2)' : '0 3px 10px rgba(0, 0, 0, 0.1)')};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  grid-column: span 2;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#ccc' : '#ddd')};
  padding-bottom: 5px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
`;

const RequiredAsterisk = styled.span`
  color: #ff0000;
  margin-left: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #f1f1f1;
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #f1f1f1;
  }

  option {
    color: #333;
    background-color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggleIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 16px;
  color: ${({ darkMode }) => (darkMode ? '#4caf50' : '#388e3c')};

  &:hover {
    color: #2e7d32;
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin: 10px 0;
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ darkMode }) => (darkMode ? '#4caf50' : '#18591d')};
  color: #fff;
  border: 1px solid ${({ darkMode }) => (darkMode ? '#388e3c' : '#144d17')};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ darkMode }) => (darkMode ? '#388e3c' : '#218838')};
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  cursor: pointer;
  padding: 5px 10px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ darkMode }) => (darkMode ? '#ff5555' : '#cc0000')};
  }
`;